/**
 * Copyright 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useMemo } from "react";
import { Box } from "@nordcloud/gnui";
import {
  OsType,
  ResourcesSortFields,
  SortOrder,
  CloudAccountCloudProvider,
} from "~/generated/graphql";
import {
  TableToolbar,
  TableToolbarSearch,
  ToolbarButtonsWrapper,
} from "~/components";
import { AppliedFiltersSection } from "~/components/AppliedFiltersSection";
import { SortSelector } from "~/components/SortSelector";
import { TABLE_LABELS } from "~/constants";
import { useQueryState } from "~/hooks";
import { useAppliedFilters } from "~/hooks/useAppliedFilters";
import { createValueLabelMap } from "~/hooks/useAppliedFilters/utils";
import { isNotNil } from "~/tools";
import { useGetCloudAccounts } from "~/views/accounts/hooks/useGetCloudAccounts/useGetCloudAccounts";
import { useGetPlans } from "~/views/plans/hooks";
import { useGetAllResourceGroups } from "~/views/resourceGroups/hooks/useGetResourceGroups/useGetAllResourceGroups";
import { ResourceQuery } from "../types";
import { Actions } from "./Actions";
import { createResourcesAppliedFilters } from "./resourcesAppliedFilters";
import { ResourcesFiltersSidebar } from "./ResourcesFiltersSidebar/ResourcesFiltersSidebar";
import { ResourceSearch } from "./ResourcesSearch/ResourceSearch";

const sortOptions = [
  {
    value: ResourcesSortFields.Name,
    label: "Resource Name",
  },
  {
    value: ResourcesSortFields.CreatedAt,
    label: TABLE_LABELS.createdAt,
  },
];

type Props = {
  resourcesDetails: {
    id: string;
    name: string;
    scanned: boolean;
    os?: OsType | null;
  }[];
  selectedResourceIds?: string[];
  allResourcesSelected?: boolean;
  hasResources?: boolean;
  provider?: CloudAccountCloudProvider;
  resultsCount?: number;
};

export function ResourceListHeader({
  resourcesDetails,
  selectedResourceIds = [],
  allResourcesSelected = false,
  hasResources = false,
  provider,
  resultsCount = 0,
}: Props) {
  const { queryState, updateQueryState } = useQueryState<ResourceQuery>();
  const { field, order } = queryState;
  const { data: plans, loading: plansLoading } = useGetPlans({
    page: "1",
    limit: "1000",
    basic: true,
  });
  const { data: cloudAccounts, loading: cloudAccountsLoading } =
    useGetCloudAccounts({
      page: "1",
      limit: "1000",
    });
  const {
    getGroups,
    resourceGroups,
    loading: resourceGroupsLoading,
  } = useGetAllResourceGroups({
    basic: true,
  });
  useEffect(() => {
    getGroups();
  }, [getGroups]);
  const planNamesById = useMemo(
    () =>
      createValueLabelMap(
        plans,
        (plan) => plan.id,
        (plan) => plan.name
      ),
    [plans]
  );
  const resourcesAppliedFilters = useMemo(
    () =>
      createResourcesAppliedFilters(
        planNamesById,
        createValueLabelMap(
          cloudAccounts.filter(isNotNil),
          (cloudAccount) => cloudAccount.providerId ?? "",
          (cloudAccount) => cloudAccount.name ?? ""
        ),
        createValueLabelMap(
          resourceGroups,
          (resourceGroup) => resourceGroup.id,
          (resourceGroup) => resourceGroup.name
        )
      ),
    [planNamesById, cloudAccounts, resourceGroups]
  );
  const planCheckboxes = useMemo(
    () =>
      plans.map((plan) => ({
        value: plan.id,
        label: plan.name,
      })),
    [plans]
  );
  const cloudAccountCheckboxes = useMemo(
    () =>
      cloudAccounts.filter(isNotNil).map((cloudAccount) => ({
        value: cloudAccount.providerId ?? "",
        label: cloudAccount.name ?? "",
      })),
    [cloudAccounts]
  );
  const resourceGroupCheckboxes = useMemo(
    () =>
      resourceGroups.map((resourceGroup) => ({
        value: resourceGroup.id,
        label: resourceGroup.name,
      })),
    [resourceGroups]
  );

  const appliedFiltersState = useAppliedFilters({
    queryState,
    updateQueryState,
    definitions: resourcesAppliedFilters,
  });

  const handleSortDirection = (sortOrder: SortOrder) => {
    updateQueryState({
      order: sortOrder,
    });
  };

  const handleSortField = (sortField: ResourcesSortFields) => {
    updateQueryState({
      field: sortField,
    });
  };

  return (
    <Box boxStyle="lightGrey" spacing="spacing04">
      <TableToolbar>
        <SortSelector<ResourcesSortFields>
          options={sortOptions}
          value={field ?? ResourcesSortFields.Name}
          direction={order ?? SortOrder.Asc}
          onSelect={handleSortField}
          onSortChange={handleSortDirection}
        />
        <TableToolbarSearch>
          <ResourceSearch />
        </TableToolbarSearch>
        <ToolbarButtonsWrapper>
          <ResourcesFiltersSidebar
            provider={provider}
            resultsCount={resultsCount}
            planCheckboxes={planCheckboxes}
            cloudAccountCheckboxes={cloudAccountCheckboxes}
            resourceGroupCheckboxes={resourceGroupCheckboxes}
            plansLoading={plansLoading}
            cloudAccountsLoading={cloudAccountsLoading}
            resourceGroupsLoading={resourceGroupsLoading}
          />
          <Actions
            size="large"
            {...{ resourcesDetails }}
            selectedResourceIds={selectedResourceIds}
            allResourcesSelected={allResourcesSelected}
            hasResources={hasResources}
          />
        </ToolbarButtonsWrapper>
      </TableToolbar>
      <AppliedFiltersSection state={appliedFiltersState} />
    </Box>
  );
}
